// Export & backup (§6.19) — generazione LATO CLIENT (scelta di progetto):
// PDF unico (jsPDF) o ZIP cifrato (zip.js), librerie caricate ON-DEMAND da CDN.
import { euro, dateIT, DOC_LABEL, fieldLabel } from './format';
import type { DocumentDTO, WarrantyDTO } from '@shared/types';

const JSPDF_URL = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js';
const ZIPJS_URL = 'https://cdn.jsdelivr.net/npm/@zip.js/zip.js@2.7.52/dist/zip.min.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    jspdf?: { jsPDF: new (opts?: any) => any };
    zip?: any;
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Impossibile caricare ${src}`));
    document.head.appendChild(s);
  });
}

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Testo strutturato dell'archivio (riusato da PDF e ZIP/JSON). */
function buildText(docs: DocumentDTO[], warr: WarrantyDTO[]): string {
  const lines: string[] = ['ARCHIVIO DOCUMENTALE — Export', `Data: ${new Date().toLocaleString('it-IT')}`, ''];
  lines.push('— DOCUMENTI —');
  for (const d of docs) {
    lines.push(`• ${d.title} (${DOC_LABEL[d.type]}) — ${d.holder}`);
    if (d.number) lines.push(`   Numero: ${d.number}`);
    if (d.expires_at) lines.push(`   Scadenza: ${dateIT(d.expires_at)}`);
    for (const [k, v] of Object.entries(d.fields)) lines.push(`   ${fieldLabel(k)}: ${v}`);
  }
  lines.push('', '— GARANZIE —');
  for (const w of warr) {
    lines.push(`• ${w.product}${w.seller ? ` — ${w.seller}` : ''}`);
    if (w.amount_cents != null) lines.push(`   Importo: ${euro(w.amount_cents)}`);
    lines.push(`   Acquisto: ${dateIT(w.purchased_at)} · Scadenza: ${dateIT(w.expires_at)}`);
  }
  return lines.join('\n');
}

/** PDF unico con i dati testuali di documenti e garanzie. */
export async function exportPdf(docs: DocumentDTO[], warr: WarrantyDTO[]): Promise<void> {
  await loadScript(JSPDF_URL);
  const ctor = window.jspdf?.jsPDF;
  if (!ctor) throw new Error('jsPDF non disponibile');
  const doc = new ctor({ unit: 'pt', format: 'a4' });
  const margin = 48;
  const width = doc.internal.pageSize.getWidth() - margin * 2;
  const pageH = doc.internal.pageSize.getHeight();
  let y = margin;

  const write = (text: string, size: number, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    for (const line of doc.splitTextToSize(text, width)) {
      if (y > pageH - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += size + 6;
    }
  };

  write('Archivio Documentale — Export', 18, true);
  write(new Date().toLocaleString('it-IT'), 10);
  y += 8;
  write('Documenti', 14, true);
  for (const d of docs) {
    write(`${d.title} — ${d.holder}`, 11, true);
    if (d.number) write(`Numero: ${d.number}`, 10);
    if (d.expires_at) write(`Scadenza: ${dateIT(d.expires_at)}`, 10);
    for (const [k, v] of Object.entries(d.fields)) write(`${fieldLabel(k)}: ${v}`, 10);
    y += 4;
  }
  y += 8;
  write('Garanzie', 14, true);
  for (const w of warr) {
    write(`${w.product}${w.seller ? ` — ${w.seller}` : ''}`, 11, true);
    if (w.amount_cents != null) write(`Importo: ${euro(w.amount_cents)}`, 10);
    write(`Acquisto: ${dateIT(w.purchased_at)} · Scadenza: ${dateIT(w.expires_at)}`, 10);
    y += 4;
  }

  download(doc.output('blob'), `archivio-export-${today()}.pdf`);
}

/** ZIP (opzionalmente cifrato con password AES) con JSON + testo dell'archivio. */
export async function exportZip(
  docs: DocumentDTO[],
  warr: WarrantyDTO[],
  password?: string,
): Promise<void> {
  await loadScript(ZIPJS_URL);
  const zip = window.zip;
  if (!zip) throw new Error('zip.js non disponibile');
  zip.configure({ useWebWorkers: false });

  const writer = new zip.ZipWriter(new zip.BlobWriter('application/zip'), password ? { password, encryptionStrength: 3 } : {});
  const json = JSON.stringify({ exported_at: new Date().toISOString(), documents: docs, warranties: warr }, null, 2);
  await writer.add('archivio.json', new zip.TextReader(json));
  await writer.add('archivio.txt', new zip.TextReader(buildText(docs, warr)));
  const blob = await writer.close();
  download(blob, `archivio-export-${today()}.zip`);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
